/**
 * Legacy vs split, side by side — PD-5, spec §5.5 and §11's "parity view".
 *
 * One scenario, run twice: once with the legacy agent driving the chat, once on
 * the split plane. For every side that submitted on BOTH paths, this puts the
 * two submitted stories next to each other in the existing `SideBySide` — the
 * same fields, the same layout the match comparison uses, with the mirror note
 * turned off because both cards are the same person.
 *
 * The stories are re-read LIVE from `/stories/:id`. A run row stores ids, never
 * content: the comparison has to show what the matcher currently holds, not
 * what a receipt claimed it held at the time.
 *
 * A side that submitted on only one path is not drawn as half a comparison. It
 * is listed as a gap, because "the split path never submitted for side B" is
 * one of the most interesting things this screen can find.
 */
import { useEffect, useState } from 'react';
import { getStoryWithMatches } from '../../api/client';
import SideBySide from '../comparison/SideBySide';
import type { ScenarioRun, StoryResponse } from '../../api/types';
import { comparableSides, textStats } from './run-model';

interface Props {
  legacy: ScenarioRun | null;
  split: ScenarioRun | null;
}

function coverage(a: StoryResponse | undefined, b: StoryResponse | undefined): string | null {
  if (!a || !b) return null;
  const l = textStats(a.rawText);
  const s = textStats(b.rawText);
  return `submitted text — legacy ${l.words} words / ${l.chars} chars · split ${s.words} words / ${s.chars} chars`;
}

export default function PathComparison({ legacy, split }: Props) {
  const { pairs, incomparable } = comparableSides(legacy, split);
  const [stories, setStories] = useState<Record<string, StoryResponse>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const ids = pairs.flatMap((p) => [p.legacy.storyId, p.split.storyId]).filter(Boolean) as string[];
  const key = ids.join(',');

  useEffect(() => {
    if (ids.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(
      [...new Set(ids)].map((id) =>
        getStoryWithMatches(id).then((res) => [id, res.story] as const),
      ),
    )
      .then((entries) => {
        if (cancelled) return;
        setStories(Object.fromEntries(entries));
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // `key` is the identity of the id SET — re-reading on every render of the
    // same two runs would hammer the matcher from a screen that is just open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!legacy || !split) {
    return (
      <p className="text-sm text-gray-500" data-testid="path-comparison-empty">
        Pick a run on each path to compare them.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="path-comparison">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          Could not read the stories: {error}
        </div>
      )}
      {loading && <p className="text-sm text-gray-500">Loading stories…</p>}

      {incomparable.length > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800"
          data-testid="path-comparison-gaps"
        >
          No comparison for {incomparable.join(', ')} — that side has a submitted story on only one
          of the two paths.
        </div>
      )}

      {pairs.map((pair) => {
        const a = stories[pair.legacy.storyId as string];
        const b = stories[pair.split.storyId as string];
        if (!a || !b) return null;
        return (
          <div key={pair.label} className="space-y-1" data-testid="path-comparison-pair" data-label={pair.label}>
            <SideBySide
              storyA={a}
              storyB={b}
              title={`Side ${pair.label} — legacy vs split`}
              labelA="legacy path"
              labelB="split path"
              subtitleA={`${pair.legacy.storyId} · ${legacy.startedAt}`}
              subtitleB={`${pair.split.storyId} · ${split.startedAt}`}
              showMirror={false}
            />
            <p className="text-xs text-gray-500 px-1" data-testid="path-comparison-coverage">
              {coverage(a, b)}
            </p>
          </div>
        );
      })}

      {pairs.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="path-comparison-none">
          These two runs share no side with a submitted story on both paths.
        </p>
      )}
    </div>
  );
}
